
import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Map as MapIcon, FileText, Loader2, CheckCircle2, ShieldAlert, ChevronRight, Layers, Route, Info, AlertTriangle, Eye, Mountain, Flame, Activity, Globe, Lock, Camera, Trash2, Link as LinkIcon, Plus, Video, Image as ImageIcon, Save, Sparkles, Wand2, RefreshCw } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { compressImages } from '../utils/imageCompression';
import { uploadGpxRoute, fetchGpxWithProxy, updateGpxRoute, deleteRoute, analyzeRouteServer, storage, fetchAllRoutes } from '../services/firebaseService';
import { generateRouteMetadata, analyzeExternalLink, classifyWaypoints, classifyRouteSuitability } from '../services/geminiService';
import type { RouteSuitability } from '../services/geminiService';
import { fetchElevation } from '../services/elevationService';
import { getDistance } from '../services/geometryUtils';
import { performRouteAnalysis, determineRegion } from '../services/routeAnalysis';
import { useTranslation } from 'react-i18next';
import { isAdminUser } from '../services/accessControl';
import { AppUser, RouteDifficulty, RouteStatus, RouteRegion, RouteExternalLink, ElevationPoint, Coordinates, GpxRoute, RouteStats, WikiSpot, RouteWaypoint, WaypointIcon, VehicleCategory, TerrainPreference } from '../types';
import { MapContainer, TileLayer, Polyline, useMap, Pane, Marker, Tooltip } from 'react-leaflet';
import { getWaypointMeta } from '../services/waypointService';
import { MAP_LAYERS } from '../constants';
import L from 'leaflet';

import { BRAND_DARK } from '../constants/mapColors';
interface RouteUploadModalProps {
  user: AppUser;
  onClose: () => void;
  onSuccess: (routeId?: string) => void;
  editingRoute?: GpxRoute | null;
  preloadedFile?: File | null;
  initialMetadata?: { name: string, description?: string };
}

interface RouteSegment {
  id: string;
  name: string;
  description: string;
  difficulty: RouteDifficulty;
  blob: Blob;
  pointsCount: number;
  coordinates: [number, number][][];
  elevations: number[];
  elevationProfile: ElevationPoint[];
  distanceKm: number;
  estimatedTime: string;
  isAiGenerated?: boolean;
  stats?: RouteStats;
  wikiSpots?: WikiSpot[];
  startPlace?: string;
  endPlace?: string;
  routeWaypoints?: RouteWaypoint[];
  startCoords?: Coordinates;
  endCoords?: Coordinates;
  // Vehicle suitability
  suitableCategories?: VehicleCategory[];
  terrainTypes?: TerrainPreference[];
  requires4wd?: boolean;
  maxRecommendedLengthCm?: number;
  suitabilityNote?: string;
  isSuitabilityLoading?: boolean;
  // Seasonal assessment
  bestSeasons?: string[];
  winterSuitability?: string;
  seasonalNote?: string;
}

/** Infer the best waypoint icon from GPX sym tag + waypoint name/description text. */
const inferWaypointIcon = (sym: string, name: string, description?: string): WaypointIcon => {
  const s = sym.toLowerCase();
  const txt = `${name} ${description || ''}`.toLowerCase();

  // Check GPX sym tag first
  if (s && s !== 'waypoint' && s !== 'flag' && s !== 'default') {
    if (/parking|car|p-plass|rasteplats/.test(s)) return 'parking';
    if (/camp|tent|shelter|hut|cabin|stuga/.test(s)) return 'camp';
    if (/water|drinking|spring|fountain|lake/.test(s)) return 'water';
    if (/summit|peak|scenic|viewpoint|lookout|panorama/.test(s)) return 'viewpoint';
    if (/danger|caution|warning/.test(s)) return 'danger';
    if (/restaurant|food|cafe|eating|bar/.test(s)) return 'food';
    if (/star|favorite|poi|attraction|church|museum/.test(s)) return 'star';
  }

  // Name/description keywords — Norwegian, Swedish, English with inflected forms
  if (/parker(?:ing|ingsplats)?|p[\s-]?plass|car\s?park|trailhead|startpunkt|rasteplats|rasteplass|rastplats/i.test(txt)) return 'parking';
  if (/telt(?:plass)?|leir|hytt[ea]n?|koie[nr]?|gapahuk|cabin|hut|shelter|camp(?:ing)?|overnatting|bivuakk|dnt|stug(?:a|an|or)|vindskydd|fjällstation|fjellstue|seter|støl/i.test(txt)) return 'camp';
  if (/vann(?:et)?|vatn|bekk(?:en)?|elv(?:a|en)?|foss(?:en)?|kilde|spring|water|drinking|innsjø|tjern|brønn|sjö[en]?|älv(?:en)?|bäck(?:en)?|källa[rn]?|vattenfall/i.test(txt)) return 'water';
  if (/utsikt(?:s(?:punkt|plats|torn))?|topp(?:en)?|tind(?:en)?|fjell(?:et)?|peak|summit|viewpoint|panorama|varde[nr]?|høgste|eggen|nutane?|piggen?|berg(?:et)?|kullen?|ås(?:en)?/i.test(txt)) return 'viewpoint';
  if (/fare|danger|steinras|varsling|caution|bro[en]?n?|bridge|kryss(?:ning)?|crossing|varning|korsning/i.test(txt)) return 'danger';
  if (/mat(?:butik)?|kafé|kafe|restaurant|kiosk|butik[ken]?|spisested|food|café?|diner|pub|bar|fika|konditori|värdshus|servering/i.test(txt)) return 'food';
  if (/kirke[rn]?|church|museum|sever|attrak|stav(?:kirke|kyrka)|historisk|monument|ruin(?:er)?|fyr(?:et|en|tårn)?|lighthouse|slott(?:et)?|castle|kyrk(?:a|an)|forn(?:lämning|minne)|grav(?:högar?|hauger?)|herrgård|borg(?:en)?|kloster|gruv(?:a|an)/i.test(txt)) return 'star';

  return 'pin';
};

const DIFFICULTIES: { label: RouteDifficulty, icon: React.ReactNode, color: string }[] = [
  { label: 'Easy', icon: <CheckCircle2 size={14} />, color: 'text-emerald-500' },
  { label: 'Medium', icon: <Activity size={14} />, color: 'text-blue-500' },
  { label: 'Hard', icon: <AlertTriangle size={14} />, color: 'text-orange-500' },
  { label: 'Extreme', icon: <Flame size={14} />, color: 'text-red-500' }
];

const CATEGORY_LABELS: Record<VehicleCategory, { emoji: string; label: string }> = {
  motorhome: { emoji: '🚐', label: 'Bobil' },
  campervan: { emoji: '🚌', label: 'Campervan' },
  caravan: { emoji: '🏠', label: 'Campingvogn' },
  camper_trailer: { emoji: '🪝', label: 'Campinghenger' },
  rooftent: { emoji: '⛺', label: 'Taktelt' },
  offroad: { emoji: '🚙', label: 'Offroad/4x4' },
  car: { emoji: '🚗', label: 'Personbil' },
};

const TERRAIN_LABELS: Record<TerrainPreference, { emoji: string; label: string }> = {
  paved: { emoji: '🛣️', label: 'Asfalt' },
  gravel: { emoji: '🪨', label: 'Grus' },
  forest: { emoji: '🌲', label: 'Skog' },
  sand: { emoji: '🏖️', label: 'Sand' },
  snow: { emoji: '❄️', label: 'Snø' },
  mountain: { emoji: '⛰️', label: 'Fjell' },
  coastal: { emoji: '🌊', label: 'Kyst' },
  offroad: { emoji: '🚜', label: 'Offroad' },
};

const MapController: React.FC<{ coords: [number, number][] | [number, number][][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    // Wait for modal transition/expansion to finish before calculating bounds
    const timer = setTimeout(() => {
      map.invalidateSize();

      if (coords && coords.length > 0) {
        const flatCoords = (Array.isArray(coords[0][0]) ? (coords as [number, number][][]).flat() : coords) as [number, number][];
        if (flatCoords.length > 1) {
          const bounds = L.latLngBounds(flatCoords);
          // Use ample padding to ensure route is fully visible
          map.fitBounds(bounds, { padding: [80, 80], animate: true, duration: 1.0 });
        }
      }
    }, 600); // 600ms delay to ensure DOM is stable

    return () => clearTimeout(timer);
  }, [map, coords]);
  return null;
};

// Missing helper
const getStatusColor = (status: RouteStatus) => {
  switch (status) {
    case 'public': return 'bg-emerald-500/10 border-emerald-500/50 text-emerald-500';
    case 'pending': return 'bg-amber-500/10 border-amber-500/50 text-amber-500';
    case 'private': return 'bg-stone-500/10 border-stone-500/50 text-[var(--text-muted)]';
    case 'rejected': return 'bg-red-500/10 border-red-500/50 text-red-500';
    default: return 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)]';
  }
};

const RouteUploadModal: React.FC<RouteUploadModalProps> = ({ user, onClose, onSuccess, editingRoute, preloadedFile, initialMetadata }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'upload' | 'configure' | 'photos' | 'links'>('upload');
  const [segments, setSegments] = useState<RouteSegment[]>([]);
  const segmentsRef = useRef<RouteSegment[]>([]);
  segmentsRef.current = segments;
  const [currentSegmentIdx, setCurrentSegmentIdx] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<{ name: string; id: string; distance: number } | null>(null);
  const [mapMode, setMapMode] = useState<'topo' | 'satellite'>('topo');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photosInputRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const [uploadStatus, setUploadStatus] = useState<RouteStatus>('private');
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const [externalLinks, setExternalLinks] = useState<RouteExternalLink[]>([]);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [isAnalyzingLink, setIsAnalyzingLink] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [classifyingWpIdx, setClassifyingWpIdx] = useState<number>(-1); // -1 = not classifying

  const isAdmin = isAdminUser(user);
  const currentSeg = segments[currentSegmentIdx];

  // Helper functions
  const updateCurrentSegment = (updates: Partial<RouteSegment>) => {
    setSegments(prev => prev.map((s, i) => i === currentSegmentIdx ? { ...s, ...updates } : s));
  };

  // Flush pending input values from DOM refs to state (fixes mobile keyboard race condition)
  const flushInputRefs = () => {
    const updates: Partial<RouteSegment> = {};
    if (nameRef.current) updates.name = nameRef.current.value;
    if (descriptionRef.current) updates.description = descriptionRef.current.value;
    if (Object.keys(updates).length > 0) updateCurrentSegment(updates);
    return updates;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (step === 'upload') {
      const file = files[0];
      const name = file.name.toLowerCase();
      if (name.endsWith('.gpx')) {
        processGpxFile(file);
      } else if (name.endsWith('.kml')) {
        convertKmlToGpxFile(file).then(gpxFile => processGpxFile(gpxFile)).catch(e => setError(e.message));
      } else {
        setError("Ugyldig filformat. Kun .gpx- og .kml-filer støttes.");
      }
    } else if (step === 'photos') {
      const newFiles = Array.from(files) as File[];
      setSelectedPhotos(prev => [...prev, ...newFiles]);
      const newPreviews = newFiles.map(f => URL.createObjectURL(f));
      setPhotoPreviews(prev => [...prev, ...newPreviews]);
    }
    // Reset value to allow selecting same file again
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    const urlToRemove = photoPreviews[index];
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));

    if (urlToRemove.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRemove);
      const blobUrls = photoPreviews.filter(p => p.startsWith('blob:'));
      const blobIndexToRemove = blobUrls.indexOf(urlToRemove);
      if (blobIndexToRemove !== -1) {
        setSelectedPhotos(prev => prev.filter((_, i) => i !== blobIndexToRemove));
      }
    }
  };

  const handleAutoSuggest = async () => {
    if (!currentSeg) return;
    setIsGenerating(true);
    setStatusMessage("Genererer tekst med AI...");
    setError(null);

    try {
      const coords = currentSeg.coordinates.flat();
      if (coords.length < 2) {
        throw new Error("Ruten har for få punkter.");
      }

      const startCoord = coords[0] as [number, number]; // [lat, lon]
      const endCoord = coords[coords.length - 1] as [number, number];

      // Sample intermediate waypoints for richer AI context
      const waypointCount = Math.min(5, Math.floor(coords.length / 2));
      const waypoints: [number, number][] = [];
      for (let i = 1; i <= waypointCount; i++) {
        const idx = Math.floor((i / (waypointCount + 1)) * coords.length);
        waypoints.push(coords[idx] as [number, number]);
      }

      // Include imported GPX waypoints (these have real names — better for AI context)
      if (currentSeg.routeWaypoints?.length) {
        for (const wp of currentSeg.routeWaypoints.slice(0, 10)) {
          waypoints.push([wp.latitude, wp.longitude]);
        }
      }

      // Extract explicit waypoint names to send directly to AI
      const waypointNames = currentSeg.routeWaypoints
        ?.map(wp => wp.name)
        .filter(n => n && n !== 'Waypoint') || [];

      // Server-side geocoding + AI generation
      const metadata = await generateRouteMetadata(
        startCoord, endCoord, currentSeg.distanceKm,
        waypoints, currentSeg.elevationProfile, waypointNames
      );

      const newName = metadata?.title || 'Ny rute';
      const newDesc = metadata?.description || `${currentSeg.distanceKm.toFixed(1)} km, ca ${Math.max(1, Math.round((currentSeg.distanceKm / 50) * 10) / 10)} t.`;

      if (metadata?.title && metadata?.description) {
        updateCurrentSegment({
          name: newName,
          description: newDesc,
          startPlace: metadata.startPlace,
          endPlace: metadata.endPlace,
          ...(metadata.bestSeasons && { bestSeasons: metadata.bestSeasons }),
          ...(metadata.winterSuitability && { winterSuitability: metadata.winterSuitability }),
          ...(metadata.seasonalNote && { seasonalNote: metadata.seasonalNote }),
        });
      } else {
        updateCurrentSegment({ name: newName, description: newDesc });
        setStatusMessage("AI utilgjengelig — bruker stedsnavn-basert tekst.");
        setTimeout(() => setStatusMessage(null), 3000);
      }

      // Only run suitability if not already classified (auto-trigger on upload already handles this)
      if (!currentSeg.suitableCategories?.length) {
        setStatusMessage('Vurderer kjøretøyegnethet...');
        try {
          let maxElev = 0, gain = 0;
          const prof = currentSeg.elevationProfile || [];
          for (let i = 0; i < prof.length; i++) {
            if (prof[i].elevation > maxElev) maxElev = prof[i].elevation;
            if (i > 0) { const d = prof[i].elevation - prof[i - 1].elevation; if (d > 0) gain += d; }
          }
          const flatForRegion = currentSeg.coordinates.flat();
          const mid = flatForRegion[Math.floor(flatForRegion.length / 2)] || flatForRegion[0];
          const region = mid ? determineRegion(mid[0], mid[1]) : undefined;

          const suitResult = await classifyRouteSuitability({
            name: newName, description: newDesc, distanceKm: currentSeg.distanceKm,
            elevationGain: Math.round(gain), maxElevation: Math.round(maxElev), region,
          });
          if (suitResult) {
            updateCurrentSegment({
              suitableCategories: suitResult.suitableCategories, terrainTypes: suitResult.terrainTypes,
              requires4wd: suitResult.requires4wd, maxRecommendedLengthCm: suitResult.maxRecommendedLengthCm,
              suitabilityNote: suitResult.suitabilityNote,
            });
          }
        } catch (e: any) {
          console.warn('[handleAutoSuggest] Suitability feilet:', e?.message);
        }
      }
    } catch (e: any) {
      console.error(e);
      const hours = currentSeg?.distanceKm ? Math.max(1, Math.round((currentSeg.distanceKm / 50) * 10) / 10) : 1;
      updateCurrentSegment({
        name: 'Ny rute',
        description: `${(currentSeg?.distanceKm || 0).toFixed(1)} km, ca ${hours} t.`
      });
      setStatusMessage("Kunne ikke generere tekst. Fyll inn manuelt.");
      setTimeout(() => setStatusMessage(null), 3000);
    } finally {
      setIsGenerating(false);
      setStatusMessage(null);
    }
  };

  const convertKmlToGpxFile = async (kmlFile: File): Promise<File> => {
    const text = await kmlFile.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'application/xml');
    const docName = xml.querySelector('Document > name')?.textContent?.trim() || kmlFile.name.replace(/\.kml$/i, '');

    const trackPoints: { lat: number; lon: number }[] = [];
    const waypoints: { lat: number; lon: number; name: string; desc: string }[] = [];

    const placemarks = xml.querySelectorAll('Placemark');
    placemarks.forEach(pm => {
      const pmName = pm.querySelector('name')?.textContent?.trim() || 'Waypoint';
      const pmDesc = pm.querySelector('description')?.textContent?.trim() || '';
      const point = pm.querySelector('Point > coordinates');
      const lineCoords = pm.querySelector('LineString > coordinates');

      if (point && !lineCoords) {
        const parts = (point.textContent?.trim() || '').split(',');
        if (parts.length >= 2) {
          const lon = parseFloat(parts[0]), lat = parseFloat(parts[1]);
          if (!isNaN(lat) && !isNaN(lon)) waypoints.push({ lat, lon, name: pmName, desc: pmDesc });
        }
      }
      if (lineCoords) {
        const tuples = (lineCoords.textContent?.trim() || '').split(/\s+/).filter(Boolean);
        for (const tuple of tuples) {
          const parts = tuple.split(',');
          if (parts.length >= 2) {
            const lon = parseFloat(parts[0]), lat = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lon)) trackPoints.push({ lat, lon });
          }
        }
      }
    });

    // Fallback: if no LineString found, collect all <coordinates>
    if (trackPoints.length === 0) {
      xml.querySelectorAll('coordinates').forEach(el => {
        const tuples = (el.textContent?.trim() || '').split(/\s+/).filter(Boolean);
        for (const tuple of tuples) {
          const parts = tuple.split(',');
          if (parts.length >= 2) {
            const lon = parseFloat(parts[0]), lat = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lon)) trackPoints.push({ lat, lon });
          }
        }
      });
    }

    if (trackPoints.length < 2) throw new Error('KML-filen inneholder for få punkter (minimum 2).');
    const esc = (s: string) => s.replace(/[<>&"']/g, '');
    const wptXml = waypoints.slice(0, 50).map(w =>
      `<wpt lat="${w.lat}" lon="${w.lon}"><name>${esc(w.name)}</name>${w.desc ? `<desc>${esc(w.desc)}</desc>` : ''}<sym>Waypoint</sym></wpt>`
    ).join('');
    const trkpts = trackPoints.map(p => `<trkpt lat="${p.lat}" lon="${p.lon}"></trkpt>`).join('');
    const gpxXml = `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="Fricamp" xmlns="http://www.topografix.com/GPX/1/1">${wptXml}<trk><name>${esc(docName)}</name><trkseg>${trkpts}</trkseg></trk></gpx>`;
    return new File([gpxXml], kmlFile.name.replace(/\.kml$/i, '.gpx'), { type: 'application/gpx+xml' });
  };

  const processGpxFile = async (file: File) => {
    setError(null);
    setIsGenerating(true);
    setStatusMessage("Analyserer GPX-fil...");

    try {
      const text = await file.text();
      const parser = new DOMParser();
      const dom = parser.parseFromString(text, "text/xml");

      if (dom.getElementsByTagName("parsererror").length > 0) {
        throw new Error("Filen inneholder XML-feil.");
      }

      const trkElements = Array.from(dom.getElementsByTagName("trk"));
      const rteElements = Array.from(dom.getElementsByTagName("rte"));
      const allCandidates = [...trkElements, ...rteElements];

      if (allCandidates.length === 0) {
        throw new Error("Ingen spor funnet.");
      }

      // Extract <wpt> elements (root-level waypoints in GPX)
      const wptElements = Array.from(dom.getElementsByTagName('wpt'));
      const extractedWaypoints: RouteWaypoint[] = wptElements
        .map(wpt => {
          const lat = parseFloat(wpt.getAttribute('lat') || '0');
          const lon = parseFloat(wpt.getAttribute('lon') || '0');
          if (!lat || !lon) return null;
          const name = wpt.getElementsByTagName('name')[0]?.textContent?.trim() || 'Waypoint';
          const desc = wpt.getElementsByTagName('desc')[0]?.textContent?.trim() || undefined;
          const eleTxt = wpt.getElementsByTagName('ele')[0]?.textContent?.trim();
          const sym = wpt.getElementsByTagName('sym')[0]?.textContent?.trim() || '';
          return { latitude: lat, longitude: lon, name, description: desc, elevation: eleTxt ? parseFloat(eleTxt) : undefined, icon: inferWaypointIcon(sym, name, desc) } as RouteWaypoint;
        })
        .filter(Boolean)
        .slice(0, 50) as RouteWaypoint[];

      // Pick only the element with the most points (avoids duplicate trk+rte from GPS apps)
      let bestElement = allCandidates[0];
      let bestCount = 0;
      for (const el of allCandidates) {
        const pts = el.getElementsByTagName("trkpt").length + el.getElementsByTagName("rtept").length;
        if (pts > bestCount) { bestCount = pts; bestElement = el; }
      }
      const allElements = [bestElement];

      const extractedSegments: RouteSegment[] = [];

      for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i];
        let trkName = el.getElementsByTagName("name")[0]?.textContent || `Rute ${i + 1}`;

        if (initialMetadata?.name && allElements.length === 1) {
          trkName = initialMetadata.name;
        }

        let trksegs = Array.from(el.getElementsByTagName("trkseg"));
        if (trksegs.length === 0) {
          trksegs = [el];
        }

        const allSegmentCoords: [number, number][][] = [];
        const flatElevations: number[] = [];
        const flatCoords: [number, number][] = [];
        let hasElevationData = false;

        let segmentsXml = '';

        for (const seg of trksegs) {
          const ptTag = seg.getElementsByTagName("trkpt").length > 0 ? "trkpt" : "rtept";
          const pts = Array.from(seg.getElementsByTagName(ptTag));
          const segCoords: [number, number][] = [];

          let segXml = '';
          for (const pt of pts) {
            const lat = parseFloat(pt.getAttribute("lat") || "0");
            const lon = parseFloat(pt.getAttribute("lon") || "0");
            const eleTxt = pt.getElementsByTagName("ele")[0]?.textContent;

            if (lat && lon) {
              segCoords.push([lat, lon]);
              flatCoords.push([lat, lon]);
              if (eleTxt) {
                flatElevations.push(parseFloat(eleTxt));
                hasElevationData = true;
              } else {
                flatElevations.push(0);
              }
              segXml += `<trkpt lat="${lat}" lon="${lon}">${eleTxt ? `<ele>${eleTxt}</ele>` : '{{ELE_PLACEHOLDER}}'}</trkpt>`;
            }
          }
          if (segCoords.length > 0) {
            allSegmentCoords.push(segCoords);
            segmentsXml += `<trkseg>${segXml}</trkseg>`;
          }
        }

        if (flatCoords.length < 2) continue;

        if (!hasElevationData) {
          setStatusMessage("Henter høydedata (dette kan ta litt tid)...");
          const sampleCount = 20;
          const step = Math.max(1, Math.floor(flatCoords.length / sampleCount));

          const elevationMap = new Map<number, number>();
          const indicesToFetch = [];

          for (let k = 0; k < flatCoords.length; k += step) indicesToFetch.push(k);
          if (indicesToFetch[indicesToFetch.length - 1] !== flatCoords.length - 1) indicesToFetch.push(flatCoords.length - 1);

          try {
            for (let k = 0; k < indicesToFetch.length; k += 5) {
              const batch = indicesToFetch.slice(k, k + 5);
              await Promise.all(batch.map(async (idx) => {
                try {
                  const elevationPromise = fetchElevation({ latitude: flatCoords[idx][0], longitude: flatCoords[idx][1] });
                  const timeoutPromise = new Promise<null>(resolve => setTimeout(() => resolve(null), 3000));
                  const val = await Promise.race([elevationPromise, timeoutPromise]);

                  if (val !== null) elevationMap.set(idx, val);
                } catch (e) { }
              }));
            }
          } catch (e) {
            console.warn("Elevation fetch failed, continuing without height data");
          }

          let lastEle = elevationMap.get(0) || 0;
          for (let k = 0; k < flatCoords.length; k++) {
            if (elevationMap.has(k)) {
              flatElevations[k] = elevationMap.get(k)!;
              lastEle = flatElevations[k];
            } else {
              flatElevations[k] = lastEle;
            }
          }

          let elevationIdx = 0;
          segmentsXml = segmentsXml.replace(/{{ELE_PLACEHOLDER}}/g, () => {
            const val = flatElevations[elevationIdx]?.toFixed(1) || "0";
            elevationIdx++;
            return `<ele>${val}</ele>`;
          });
        }

        let totalDist = 0;
        const profile: ElevationPoint[] = [];
        const sampleRate = Math.max(1, Math.floor(flatCoords.length / 100));
        let flatIndex = 0;

        for (const seg of allSegmentCoords) {
          for (let k = 0; k < seg.length; k++) {
            if (k > 0) {
              totalDist += getDistance(seg[k - 1][0], seg[k - 1][1], seg[k][0], seg[k][1]);
            }

            if (flatIndex % sampleRate === 0 && flatElevations[flatIndex] !== undefined) {
              profile.push({ distance: totalDist, elevation: flatElevations[flatIndex] });
            }
            flatIndex++;
          }
        }

        const distKm = totalDist / 1000;
        const hours = distKm / 50;
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        const estimatedTime = `${h > 0 ? h + 't ' : ''}${m}m`;

        const wptXml = extractedWaypoints.map(w =>
          `<wpt lat="${w.latitude}" lon="${w.longitude}"><name>${w.name.replace(/[<>&"']/g, '')}</name>${w.description ? `<desc>${w.description.replace(/[<>&"']/g, '')}</desc>` : ''}${w.elevation ? `<ele>${w.elevation}</ele>` : ''}<sym>${w.icon}</sym></wpt>`
        ).join('');
        const standaloneGpx = `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="Fricamp" xmlns="http://www.topografix.com/GPX/1/1">${wptXml}<trk><name>${trkName.replace(/[<>&"']/g, '')}</name>${segmentsXml}</trk></gpx>`;

        extractedSegments.push({
          id: `seg-${Date.now()}-${i}`,
          name: trkName,
          description: initialMetadata?.description || '',
          difficulty: 'Medium',
          blob: new Blob([standaloneGpx], { type: 'application/gpx+xml' }),
          pointsCount: flatCoords.length,
          coordinates: allSegmentCoords,
          elevations: flatElevations,
          elevationProfile: profile,
          distanceKm: distKm,
          estimatedTime,
          routeWaypoints: extractedWaypoints,
          startCoords: { latitude: flatCoords[0][0], longitude: flatCoords[0][1] },
          endCoords: { latitude: flatCoords[flatCoords.length - 1][0], longitude: flatCoords[flatCoords.length - 1][1] },
        });
      }

      if (extractedSegments.length === 0) throw new Error("Ingen gyldige ruter.");

      // Duplicate route detection
      setDuplicateWarning(null);
      try {
        const allRoutes = await fetchAllRoutes();
        outer: for (const seg of extractedSegments) {
          if (!seg.startCoords || !seg.endCoords) continue;
          for (const existing of allRoutes) {
            if (editingRoute && existing.id === editingRoute.id) continue;
            if (!existing.startCoords || !existing.endCoords) continue;
            const distRatio = existing.distance && seg.distanceKm
              ? Math.abs(existing.distance - seg.distanceKm) / Math.max(existing.distance, seg.distanceKm)
              : 1;
            if (distRatio >= 0.2) continue;
            // Check both directions
            const s2s = getDistance(seg.startCoords.latitude, seg.startCoords.longitude, existing.startCoords.latitude, existing.startCoords.longitude);
            const e2e = getDistance(seg.endCoords.latitude, seg.endCoords.longitude, existing.endCoords.latitude, existing.endCoords.longitude);
            if (s2s < 2000 && e2e < 2000) {
              setDuplicateWarning({ name: existing.name, id: existing.id, distance: Math.round(Math.max(s2s, e2e)) });
              break outer;
            }
            const s2e = getDistance(seg.startCoords.latitude, seg.startCoords.longitude, existing.endCoords.latitude, existing.endCoords.longitude);
            const e2s = getDistance(seg.endCoords.latitude, seg.endCoords.longitude, existing.startCoords.latitude, existing.startCoords.longitude);
            if (s2e < 2000 && e2s < 2000) {
              setDuplicateWarning({ name: existing.name, id: existing.id, distance: Math.round(Math.max(s2e, e2s)) });
              break outer;
            }
          }
        }
      } catch (e) { console.warn('Duplicate check failed:', e); }

      setSegments(extractedSegments);
      setStep('configure');
    } catch (err: any) {
      setError(err.message || 'Feil ved analyse.');
    } finally {
      setIsGenerating(false);
      setStatusMessage(null);
    }
  };

  useEffect(() => {
    if (preloadedFile && !isGenerating && step === 'upload' && segments.length === 0) {
      processGpxFile(preloadedFile);
    }
  }, [preloadedFile]);

  // Auto-trigger AI waypoint icon classification when GPX is loaded
  useEffect(() => {
    if (step !== 'configure' || segments.length === 0) return;
    const seg = segments[currentSegmentIdx];
    if (!seg?.routeWaypoints?.length) return;
    // Only classify if there are 'pin' icons (i.e. unclassified)
    const hasUnclassified = seg.routeWaypoints.some(wp => wp.icon === 'pin');
    if (!hasUnclassified) return;

    let cancelled = false;
    const run = async () => {
      const wps = seg.routeWaypoints!;
      setClassifyingWpIdx(0);
      try {
        const icons = await classifyWaypoints(
          wps.map(wp => ({ name: wp.name, description: wp.description }))
        );
        if (cancelled || !icons) { setClassifyingWpIdx(-1); return; }

        // Animate: update icons one by one with a small delay
        for (let i = 0; i < icons.length; i++) {
          if (cancelled) break;
          setClassifyingWpIdx(i);
          await new Promise(r => setTimeout(r, 120));
          if (icons[i] !== wps[i].icon) {
            setSegments(prev => {
              const updated = [...prev];
              const s = { ...updated[currentSegmentIdx] };
              const updatedWps = [...(s.routeWaypoints || [])];
              updatedWps[i] = { ...updatedWps[i], icon: icons[i] };
              s.routeWaypoints = updatedWps;
              updated[currentSegmentIdx] = s;
              return updated;
            });
          }
        }
      } catch (e) {
        console.warn('AI waypoint classification failed:', e);
      }
      if (!cancelled) setClassifyingWpIdx(-1);
    };
    run();
    return () => { cancelled = true; };
  }, [step, segments.length, currentSegmentIdx]);

  // Auto-trigger suitability classification (heuristic fallback ensures fast result even if AI is down)
  useEffect(() => {
    if (step !== 'configure' || segments.length === 0) return;
    const seg = segments[currentSegmentIdx];
    if (!seg || seg.suitableCategories?.length || seg.isSuitabilityLoading) return;

    let cancelled = false;
    const run = async () => {
      updateCurrentSegment({ isSuitabilityLoading: true });
      try {
        let maxElev = 0, gain = 0;
        if (seg.elevationProfile?.length) {
          for (let i = 0; i < seg.elevationProfile.length; i++) {
            if (seg.elevationProfile[i].elevation > maxElev) maxElev = seg.elevationProfile[i].elevation;
            if (i > 0) { const diff = seg.elevationProfile[i].elevation - seg.elevationProfile[i - 1].elevation; if (diff > 0) gain += diff; }
          }
        }
        const flatForRegion = seg.coordinates.flat();
        const regionCoord = flatForRegion[Math.floor(flatForRegion.length / 2)] || flatForRegion[0];
        const region = regionCoord ? determineRegion(regionCoord[0], regionCoord[1]) : undefined;

        const result = await classifyRouteSuitability({
          name: seg.name, description: seg.description, distanceKm: seg.distanceKm,
          elevationGain: Math.round(gain), maxElevation: Math.round(maxElev), region,
        });
        if (cancelled) return;
        if (result) {
          updateCurrentSegment({
            suitableCategories: result.suitableCategories, terrainTypes: result.terrainTypes,
            requires4wd: result.requires4wd, maxRecommendedLengthCm: result.maxRecommendedLengthCm,
            suitabilityNote: result.suitabilityNote, isSuitabilityLoading: false,
          });
        } else {
          updateCurrentSegment({ isSuitabilityLoading: false });
        }
      } catch (e: any) {
        if (!cancelled) updateCurrentSegment({ isSuitabilityLoading: false, suitabilityNote: `Feilet: ${(e?.message || 'ukjent').slice(0, 60)}` });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [step, segments.length, currentSegmentIdx]);

  useEffect(() => {
    if (editingRoute) {
      setIsGenerating(true);
      const loadExisting = async () => {
        try {
          setUploadStatus(editingRoute.status || 'private');
          setExternalLinks(editingRoute.externalLinks || []);
          if (editingRoute.imageUrls) {
            setPhotoPreviews(editingRoute.imageUrls);
          }

          const gpxText = await fetchGpxWithProxy(editingRoute.gpxUrl);
          const blob = new Blob([gpxText], { type: 'application/gpx+xml' });

          const parser = new DOMParser();
          const dom = parser.parseFromString(gpxText, "text/xml");

          const trksegs = Array.from(dom.getElementsByTagName("trkseg"));
          const allSegmentCoords: [number, number][][] = [];
          const flatElevs: number[] = [];

          if (trksegs.length > 0) {
            trksegs.forEach(seg => {
              const pts = Array.from(seg.getElementsByTagName("trkpt"));
              const segCoords: [number, number][] = [];
              pts.forEach(pt => {
                const lat = parseFloat(pt.getAttribute("lat") || "0");
                const lon = parseFloat(pt.getAttribute("lon") || "0");
                const ele = parseFloat(pt.getElementsByTagName("ele")[0]?.textContent || "0");
                segCoords.push([lat, lon]);
                flatElevs.push(ele);
              });
              if (segCoords.length > 0) allSegmentCoords.push(segCoords);
            });
          } else {
            const pts = Array.from(dom.getElementsByTagName("trkpt"));
            const segCoords: [number, number][] = [];
            pts.forEach(pt => {
              const lat = parseFloat(pt.getAttribute("lat") || "0");
              const lon = parseFloat(pt.getAttribute("lon") || "0");
              const ele = parseFloat(pt.getElementsByTagName("ele")[0]?.textContent || "0");
              segCoords.push([lat, lon]);
              flatElevs.push(ele);
            });
            if (segCoords.length > 0) allSegmentCoords.push(segCoords);
          }

          setSegments([{
            id: editingRoute.id,
            name: editingRoute.name,
            description: editingRoute.description,
            difficulty: editingRoute.difficulty,
            blob: blob,
            pointsCount: flatElevs.length,
            coordinates: allSegmentCoords,
            elevations: flatElevs,
            elevationProfile: editingRoute.elevationProfile || [],
            distanceKm: editingRoute.distance || 0,
            estimatedTime: editingRoute.estimatedTime || '',
            stats: editingRoute.stats,
            wikiSpots: editingRoute.wikiSpots,
            // Load existing suitability fields for editing
            suitableCategories: editingRoute.suitableCategories,
            terrainTypes: editingRoute.terrainTypes,
            requires4wd: editingRoute.requires4wd,
            maxRecommendedLengthCm: editingRoute.maxRecommendedLengthCm,
            suitabilityNote: editingRoute.suitabilityNote,
            bestSeasons: editingRoute.bestSeasons,
            winterSuitability: editingRoute.winterSuitability,
            seasonalNote: editingRoute.seasonalNote,
          }]);

          setStep('configure');
        } catch (e) {
          setError("Kunne ikke laste rutedata for redigering.");
        } finally {
          setIsGenerating(false);
        }
      };
      loadExisting();
    }
  }, [editingRoute]);

  const handleReanalyze = async () => {
    if (!currentSeg) return;
    setIsGenerating(true);

    try {
      // 1. Run suitability FIRST — heuristic fallback is instant, gives immediate visible feedback
      if (!currentSeg.suitableCategories?.length) {
        setStatusMessage('Vurderer kjøretøyegnethet...');
        try {
          let maxElev = 0, gain = 0;
          const prof = currentSeg.elevationProfile || [];
          for (let i = 0; i < prof.length; i++) {
            if (prof[i].elevation > maxElev) maxElev = prof[i].elevation;
            if (i > 0) { const d = prof[i].elevation - prof[i - 1].elevation; if (d > 0) gain += d; }
          }
          const flatForRegion = currentSeg.coordinates.flat();
          const mid = flatForRegion[Math.floor(flatForRegion.length / 2)] || flatForRegion[0];
          const region = mid ? determineRegion(mid[0], mid[1]) : undefined;

          const suitResult = await classifyRouteSuitability({
            name: currentSeg.name, description: currentSeg.description, distanceKm: currentSeg.distanceKm,
            elevationGain: Math.round(gain), maxElevation: Math.round(maxElev), region,
          });
          if (suitResult) {
            updateCurrentSegment({
              suitableCategories: suitResult.suitableCategories,
              terrainTypes: suitResult.terrainTypes,
              requires4wd: suitResult.requires4wd,
              maxRecommendedLengthCm: suitResult.maxRecommendedLengthCm,
              suitabilityNote: suitResult.suitabilityNote,
            });
          } else {
            updateCurrentSegment({ suitabilityNote: 'AI-vurdering feilet. Prøv "AI Auto-utfyll".' });
          }
        } catch (e: any) {
          console.warn('[handleReanalyze] Kjøretøyegnethet feilet:', e?.message);
          updateCurrentSegment({ suitabilityNote: `Feilet: ${(e?.message || 'ukjent').slice(0, 60)}` });
        }
      }

      // 2. Route analysis (Overpass, Wikipedia, etc.) — may be slow, has 12s timeout
      setStatusMessage("Analyserer fasiliteter langs ruten...");
      const { stats, spots } = await performRouteAnalysis(currentSeg.coordinates, setStatusMessage);

      // 3. Check if elevation profile is missing or flat
      const currentProfile = currentSeg.elevationProfile || [];
      const isFlat = currentProfile.every(p => p.elevation === 0) || currentProfile.length < 2;

      let newProfile = currentProfile;

      if (isFlat) {
        setStatusMessage("Henter manglende høydedata...");
        const flatCoords = currentSeg.coordinates.flat();
        const sampleCount = 30;
        const step = Math.max(1, Math.floor(flatCoords.length / sampleCount));
        const elevationPoints: ElevationPoint[] = [];
        let totalDist = 0;

        for (let i = 0; i < flatCoords.length; i += step) {
          const coord = flatCoords[i];
          if (i > 0) {
            const prev = flatCoords[Math.max(0, i - step)];
            totalDist += getDistance(prev[0], prev[1], coord[0], coord[1]);
          }
          const ele = await fetchElevation({ latitude: coord[0], longitude: coord[1] });
          elevationPoints.push({ distance: totalDist, elevation: ele || 0 });
        }

        if (elevationPoints.some(p => p.elevation > 0)) {
          newProfile = elevationPoints;
        }
      }

      updateCurrentSegment({
        stats: stats,
        wikiSpots: spots,
        elevationProfile: newProfile
      });

      setStatusMessage(null);

    } catch (err: any) {
      const msg = err?.message || String(err);
      // Provide specific data source info in the error
      if (msg.includes('504') || msg.includes('timeout') || msg.toLowerCase().includes('overpass')) {
        setError("Analyse feilet: Overpass (OpenStreetMap) svarte ikke. Prøv igjen om litt.");
      } else if (msg.includes('NVDB') || msg.includes('vegvesen')) {
        setError("Analyse feilet: SVV/NVDB (norsk veidata) er utilgjengelig. Ikke tilgjengelig utenfor Norge.");
      } else if (msg.includes('NOBIL') || msg.includes('charger')) {
        setError("Analyse feilet: NOBIL (ladestasjon-data) er utilgjengelig.");
      } else if (msg.includes('geonorge') || msg.includes('kommuneinfo')) {
        setError("Analyse feilet: Geonorge (norsk regiondata) er utilgjengelig utenfor Norge.");
      } else {
        setError(`Analyse feilet: ${msg.slice(0, 120)}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteRoute = async () => {
    if (!editingRoute) return;
    setIsUploading(true);
    try {
      await deleteRoute(editingRoute.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError('Sletting feilet: ' + err.message);
      setIsUploading(false);
    }
  };

  const handleFinalSubmit = async () => {
    // Use ref to avoid stale closure — description is already validated by "Neste" button.
    const segs = segmentsRef.current;
    const invalid = segs.find(s => !s.name?.trim());
    if (invalid) {
      setError('Navn mangler for et segment.');
      return;
    }

    if (uploadStatus === 'pending' && selectedPhotos.length === 0 && photoPreviews.length === 0) {
      setError("Du må ha minst ett bilde for å foreslå en fellesrute.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      let lastUploadedRouteId: string | undefined;
      if (editingRoute && segs.length === 1) {
        const seg = segs[0];

        const stats = seg.stats || editingRoute.stats;
        const wikiSpots = seg.wikiSpots || editingRoute.wikiSpots;

        const existingUrls = photoPreviews.filter(p => !p.startsWith('blob:'));

        await updateGpxRoute(editingRoute.id, {
          name: seg.name,
          description: seg.description,
          difficulty: seg.difficulty,
          status: uploadStatus,
          externalLinks: externalLinks,
          estimatedTime: seg.estimatedTime,
          stats: stats,
          wikiSpots: wikiSpots, // SAVE SPOTS
          elevationProfile: seg.elevationProfile,
          ...(seg.suitableCategories?.length ? {
            suitableCategories: seg.suitableCategories,
            terrainTypes: seg.terrainTypes || [],
            requires4wd: seg.requires4wd || false,
            maxRecommendedLengthCm: seg.maxRecommendedLengthCm,
            suitabilityNote: seg.suitabilityNote,
          } : {}),
        });

        if (selectedPhotos.length > 0) {
          const compressedPhotos = await compressImages(selectedPhotos);
          const newUrls: string[] = [];
          for (const img of compressedPhotos) {
            const imgRef = ref(storage, `routes_images/${editingRoute.id}/${Date.now()}_${img.name}`);
            const imgSnap = await uploadBytes(imgRef, img);
            const url = await getDownloadURL(imgSnap.ref);
            newUrls.push(url);
          }
          await updateGpxRoute(editingRoute.id, { imageUrls: [...existingUrls, ...newUrls] });
        } else {
          await updateGpxRoute(editingRoute.id, { imageUrls: existingUrls });
        }

        // Server-side analysis if not already analyzed
        if (!stats) {
          setStatusMessage('Analyserer fasiliteter langs ruten...');
          try { await analyzeRouteServer(editingRoute.id); } catch (e) { console.warn('Analysis failed:', e); }
        }

      } else {
        for (const seg of segs) {
          const cleanName = seg.name.replace(/[^a-z0-9æøå ]/gi, '').replace(/\s+/g, '_');
          const fileToUpload = new File([seg.blob], `${cleanName}.gpx`, { type: 'application/gpx+xml' });

          const region = determineRegion(seg.coordinates[0][0][0], seg.coordinates[0][0][1]);

          const stats = seg.stats;
          const wikiSpots = seg.wikiSpots;

          const routeId = await uploadGpxRoute(
            user,
            seg.name,
            seg.description,
            seg.difficulty,
            fileToUpload,
            uploadStatus,
            selectedPhotos,
            region,
            seg.estimatedTime,
            seg.elevationProfile,
            stats || { wildSpotsCount: 0, campsitesCount: 0, restAreasCount: 0, totalSpots: 0 },
            externalLinks,
            seg.distanceKm,
            wikiSpots,
            seg.startPlace,
            seg.endPlace,
            seg.routeWaypoints,
            seg.startCoords,
            seg.endCoords,
            seg.suitableCategories?.length ? {
              suitableCategories: seg.suitableCategories,
              terrainTypes: seg.terrainTypes,
              requires4wd: seg.requires4wd,
              maxRecommendedLengthCm: seg.maxRecommendedLengthCm,
              suitabilityNote: seg.suitabilityNote,
            } : undefined,
            seg.bestSeasons?.length || seg.winterSuitability ? {
              bestSeasons: seg.bestSeasons as ("spring" | "summer" | "autumn" | "winter")[],
              winterSuitability: (seg.winterSuitability === "good" || seg.winterSuitability === "limited" || seg.winterSuitability === "not_recommended") ? seg.winterSuitability : undefined,
              seasonalNote: seg.seasonalNote,
            } : undefined
          );
          lastUploadedRouteId = routeId;
          // Server-side analysis if not already analyzed
          if (!stats) {
            setStatusMessage('Analyserer fasiliteter langs ruten...');
            try { await analyzeRouteServer(routeId); } catch (e) { console.warn('Analysis failed:', e); }
          }
        }
      }
      onSuccess(editingRoute ? editingRoute.id : lastUploadedRouteId);
      onClose();
    } catch (err: any) {
      setError('Lagring feilet. ' + err.message);
      setIsUploading(false);
    } finally {
      setStatusMessage(null);
    }
  };

  return (
    <div className="fixed inset-0 z-toast flex items-end sm:items-center justify-center sm:p-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <div className={`relative w-full ${step !== 'upload' ? 'max-w-5xl' : 'max-w-lg'} bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-t-modal sm:rounded-modal shadow-3xl overflow-hidden flex flex-col max-h-[90dvh] transition-all duration-500`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* HEADER */}
        <div className="p-6 border-b border-[var(--border-primary)] flex justify-between items-center bg-[var(--bg-input)] shrink-0">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-2xl border ${step === 'upload' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'}`}>
              {step === 'upload' ? <Upload size={24} /> : <Route size={24} />}
            </div>
            <div>
              <h2 className="text-lg font-black text-[var(--text-primary)] tracking-tight uppercase">
                {editingRoute ? t('admin.route_upload.edit_route') : (step === 'upload' ? t('admin.route_upload.upload_route') : step === 'photos' ? t('admin.route_upload.images') : step === 'links' ? t('admin.route_upload.links_info') : t('admin.route_upload.configure'))}
              </h2>
              <p className="text-3xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
                {editingRoute ? t('admin.route_upload.update_info') : (step === 'upload' ? 'GPX / KML' : `Del ${currentSegmentIdx + 1} av ${segments.length}`)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-[var(--bg-surface-alt)] rounded-xl">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* MAP */}
          {(step !== 'upload') && currentSeg && (
            <div className="h-48 shrink-0 md:h-auto md:flex-1 relative border-b md:border-b-0 md:border-r border-[var(--border-primary)] bg-[#f4f1ea]">
              <MapContainer
                center={currentSeg.coordinates[0][0]}
                zoom={14}
                zoomControl={false}
                className="h-full w-full"
                maxZoom={21}
              >
                {mapMode === 'topo' ? (
                  <TileLayer url={MAP_LAYERS.TOPO} attribution='&copy; OpenTopoMap' maxZoom={21} maxNativeZoom={17} />
                ) : (
                  <>
                    <TileLayer url={MAP_LAYERS.SATELLITE} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                    <Pane name="labels" style={{ zIndex: 650 }}>
                      <TileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                    </Pane>
                  </>
                )}

                <Polyline
                  positions={currentSeg.coordinates as any}
                  pathOptions={{ color: BRAND_DARK, weight: 6, opacity: 1 }}
                />
                {currentSeg.routeWaypoints?.map((wp, i) => {
                  const meta = getWaypointMeta(wp.icon);
                  const isClassifying = classifyingWpIdx === i;
                  const borderColor = isClassifying ? '#a855f7' : meta.defaultColor;
                  return (
                    <Marker
                      key={`wpt-${i}`}
                      position={[wp.latitude, wp.longitude]}
                      icon={L.divIcon({
                        className: '',
                        html: isClassifying
                          ? `<div style="width:28px;height:28px;border-radius:50%;background:#fff;border:2px solid ${borderColor};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.3)"><div style="width:14px;height:14px;border:2px solid ${borderColor};border-top-color:transparent;border-radius:50%;animation:spin 0.6s linear infinite"></div></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style>`
                          : `<div style="width:28px;height:28px;border-radius:50%;background:${meta.defaultColor}20;border:2px solid ${borderColor};display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,.3)">${meta.emoji}</div>`,
                        iconSize: [28, 28],
                        iconAnchor: [14, 14],
                      })}
                    >
                      <Tooltip direction="top" offset={[0, -14]} opacity={1}>
                        <span style={{ fontWeight: 700, fontSize: 11 }}>{isClassifying ? '...' : wp.name}</span>
                      </Tooltip>
                    </Marker>
                  );
                })}
                <MapController coords={currentSeg.coordinates} />
              </MapContainer>
              <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
                <button onClick={() => setMapMode(mapMode === 'topo' ? 'satellite' : 'topo')} className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-2xl active:scale-90 transition-all text-stone-900">
                  {mapMode === 'topo' ? <Eye size={20} /> : <Mountain size={20} />}
                </button>
              </div>
            </div>
          )}

          <div className={`${step !== 'upload' ? 'w-full md:w-[420px]' : 'w-full'} overflow-y-auto p-8 space-y-6 bg-[var(--bg-input)] custom-scrollbar`}>
            {statusMessage && step !== 'upload' && (
              <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-2xl flex items-center gap-3 animate-pulse">
                <Loader2 size={16} className="animate-spin text-blue-400" />
                <p className="text-2xs font-black text-blue-400 uppercase tracking-widest">{statusMessage}</p>
              </div>
            )}

            {step === 'upload' ? (
              // Upload UI (Same as before)
              <div className="space-y-6">
                {(preloadedFile || isGenerating) ? (
                  <div className="w-full py-20 border-2 border-dashed border-emerald-500/30 bg-emerald-500/5 rounded-modal flex flex-col items-center justify-center gap-4 animate-pulse">
                    <Loader2 size={48} className="animate-spin text-emerald-500" />
                    <p className="text-sm font-black text-emerald-400 uppercase tracking-widest">{statusMessage || "Analyserer rute..."}</p>
                  </div>
                ) : (
                  <div onClick={() => !isGenerating && fileInputRef.current?.click()} className="w-full py-16 border-2 border-dashed border-[var(--border-primary)] rounded-modal flex flex-col items-center justify-center gap-4 cursor-pointer hover:border-emerald-500/30 hover:bg-[var(--bg-surface-alt)] transition-all group">
                    <div className="p-6 bg-[var(--bg-surface-alt)] rounded-full text-[var(--text-muted)] group-hover:text-emerald-400 transition-all"><Upload size={48} /></div>
                    <div className="text-center space-y-2">
                      <p className="text-sm font-black text-[var(--text-primary)] uppercase tracking-widest">{t("auto.adminrouteupload.dra_fil_hit_eller_klikk_for_a_", "Dra fil hit eller klikk for å velge")}</p>
                      <p className="text-tiny text-[var(--text-secondary)] leading-relaxed max-w-xs mx-auto">
                        {t("auto.adminrouteupload.last_opp_ruter_fra_gps_enheten", "Last opp ruter fra GPS-enheten din, Strava, Garmin, Komoot, Google My Maps eller andre apper.")}
                      </p>
                    </div>
                    <span className="px-5 py-2 bg-amber-500/20 text-amber-400 rounded-xl text-tiny font-bold border border-amber-500/30">
                      .gpx / .kml
                    </span>
                    <input ref={fileInputRef} type="file" accept=".gpx,.kml,application/gpx+xml,application/vnd.google-earth.kml+xml" className="hidden" onChange={handleFileChange} />
                  </div>
                )}
              </div>
            ) : step === 'configure' ? (
              // Configure UI (With Re-analyze button)
              <div className="space-y-6">
                {duplicateWarning && (
                  <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-2xl flex items-start gap-3">
                    <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-2xs font-black text-amber-400 uppercase tracking-widest">{t("auto.adminrouteupload.mulig_duplikat_oppdaget", "Mulig duplikat oppdaget")}</p>
                      <p className="text-2xs text-amber-300/80 mt-1">Denne ruten ligner på «{duplicateWarning.name}» (start/slutt innen {duplicateWarning.distance}m).</p>
                      <button onClick={() => setDuplicateWarning(null)} className="text-3xs text-amber-400 underline mt-1.5 font-bold">{t("auto.adminrouteupload.lukk_advarsel", "Lukk advarsel")}</button>
                    </div>
                  </div>
                )}
                {currentSeg?.routeWaypoints && currentSeg.routeWaypoints.length > 0 && (
                  <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded-2xl flex items-center gap-3">
                    <MapIcon size={14} className="text-blue-400 shrink-0" />
                    <p className="text-2xs font-black text-blue-400 uppercase tracking-widest">{currentSeg.routeWaypoints.length} veipunkter importert fra filen</p>
                  </div>
                )}
                <div className="flex gap-1.5">{segments.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i === currentSegmentIdx ? 'bg-emerald-500' : i < currentSegmentIdx ? 'bg-emerald-900' : 'bg-[var(--bg-surface-alt)]'}`} />)}</div>
                <div className="space-y-2 relative">
                  <div className="flex justify-between items-center ml-1">
                    <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t("auto.adminrouteupload.navn", "Navn")}</label>
                    <div className="flex gap-2">
                      <button onClick={handleReanalyze} disabled={isGenerating} className="flex items-center gap-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] text-3xs font-black uppercase tracking-widest transition-colors"><RefreshCw size={12} /> {t("auto.adminrouteupload.re_analyser", "Re-analyser")}</button>
                      <button onClick={handleAutoSuggest} disabled={isGenerating} className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-3xs font-black uppercase tracking-widest transition-colors"><Wand2 size={12} /> {t("auto.adminrouteupload.ai_auto_utfyll", "AI Auto-utfyll")}</button>
                    </div>
                  </div>
                  <input ref={nameRef} type="text" value={currentSeg.name} onChange={(e) => updateCurrentSegment({ name: e.target.value })} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 px-5 text-sm focus:outline-none focus:border-emerald-500 text-[var(--text-primary)] transition-all" />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t("auto.adminrouteupload.kjoretoyegnethet", "Kjøretøyegnethet")}</label>
                    {currentSeg.isSuitabilityLoading && <span className="flex items-center gap-1 text-3xs text-purple-400"><Loader2 size={10} className="animate-spin" /> {t("auto.adminrouteupload.ai_vurderer", "AI vurderer…")}</span>}
                  </div>
                  {currentSeg.suitableCategories?.length ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {(['motorhome', 'campervan', 'caravan', 'camper_trailer', 'rooftent'] as VehicleCategory[]).map(cat => {
                          const active = currentSeg.suitableCategories?.includes(cat);
                          const meta = CATEGORY_LABELS[cat];
                          return (
                            <button key={cat} onClick={() => {
                              const cur = currentSeg.suitableCategories || [];
                              updateCurrentSegment({ suitableCategories: active ? cur.filter(c => c !== cat) : [...cur, cat] });
                            }} className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-2xs font-bold transition-all ${active ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-muted)] opacity-50'}`}>
                              <span>{meta.emoji}</span>{meta.label}
                            </button>
                          );
                        })}
                      </div>
                      <label className="text-4xs font-bold text-[var(--text-muted)] uppercase tracking-wider ml-1 mt-1">{t("auto.adminrouteupload.terrengtype", "Terrengtype")}</label>
                      <div className="flex flex-wrap gap-1.5">
                        {(Object.keys(TERRAIN_LABELS) as TerrainPreference[]).map(t => {
                          const active = currentSeg.terrainTypes?.includes(t);
                          const meta = TERRAIN_LABELS[t];
                          return (
                            <button key={t} onClick={() => {
                              const cur = currentSeg.terrainTypes || [];
                              updateCurrentSegment({ terrainTypes: active ? cur.filter(x => x !== t) : [...cur, t] });
                            }} className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-3xs font-bold transition-all ${active ? 'bg-blue-500/15 border-blue-500/40 text-blue-400' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-muted)] opacity-50'}`}>
                              <span>{meta.emoji}</span>{meta.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <button onClick={() => updateCurrentSegment({ requires4wd: !currentSeg.requires4wd })} className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-3xs font-bold transition-all ${currentSeg.requires4wd ? 'bg-orange-500/15 border-orange-500/40 text-orange-400' : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-muted)] opacity-50'}`}>
                          🚙 4WD {currentSeg.requires4wd ? 'påkrevd' : 'ikke påkrevd'}
                        </button>
                        <div className="flex items-center gap-1.5">
                          <label className="text-4xs font-bold text-[var(--text-muted)] uppercase">{t("auto.adminrouteupload.maks_lengde", "Maks lengde")}</label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={currentSeg.maxRecommendedLengthCm ? (currentSeg.maxRecommendedLengthCm / 100) : ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              updateCurrentSegment({ maxRecommendedLengthCm: val > 0 ? Math.round(val * 100) : undefined });
                            }}
                            placeholder="—"
                            className="w-16 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg py-1 px-2 text-2xs text-center focus:outline-none focus:border-emerald-500 text-[var(--text-primary)]"
                          />
                          <span className="text-3xs text-[var(--text-muted)]">m</span>
                        </div>
                      </div>
                      <div className="mt-1">
                        <label className="text-4xs font-bold text-[var(--text-muted)] uppercase tracking-wider ml-1">{t("auto.adminrouteupload.ai_merknad", "AI-merknad")}</label>
                        <input
                          type="text"
                          value={currentSeg.suitabilityNote || ''}
                          onChange={(e) => updateCurrentSegment({ suitabilityNote: e.target.value || undefined })}
                          placeholder={t("auto.adminrouteupload.placeholder.f_eks_smale_svinger_bratt_stig", "F.eks. Smale svinger, bratt stigning…")}
                          className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg py-1.5 px-3 text-2xs focus:outline-none focus:border-emerald-500 text-[var(--text-primary)] mt-0.5"
                        />
                      </div>
                    </div>
                  ) : !currentSeg.isSuitabilityLoading ? (
                    <p className={`text-2xs ml-1 ${currentSeg.suitabilityNote?.startsWith('Feilet') || currentSeg.suitabilityNote?.includes('feilet') ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                      {currentSeg.suitabilityNote || 'Kjør AI Auto-utfyll eller vent på automatisk vurdering'}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t("auto.adminrouteupload.estimert_tid", "Estimert Tid")}</label>
                  <input
                    type="text"
                    value={currentSeg.estimatedTime}
                    onChange={(e) => updateCurrentSegment({ estimatedTime: e.target.value })}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 px-5 text-sm focus:outline-none focus:border-emerald-500 text-[var(--text-primary)] transition-all"
                    placeholder="Eks: 2t 30m"
                  />
                </div>

                <div className="space-y-2 relative">
                  <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t("auto.adminrouteupload.beskrivelse", "Beskrivelse")}</label>
                  <textarea ref={descriptionRef} value={currentSeg.description} onChange={(e) => updateCurrentSegment({ description: e.target.value })} className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-4 px-5 text-sm focus:outline-none focus:border-emerald-500 text-[var(--text-primary)] min-h-[120px] resize-none transition-all" />
                </div>
              </div>
            ) : step === 'photos' ? (
              // Photos UI
              <div className="space-y-6">
                <div className="bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/10">
                  <p className="text-2xs text-emerald-400 font-medium leading-relaxed">
                    {t("auto.adminrouteupload.last_opp_bilder_for_a_gjore_ru", "Last opp bilder for å gjøre ruten mer attraktiv. Bilder hjelper andre med å vurdere terrenget.")}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => photosInputRef.current?.click()} className="aspect-square rounded-2xl border-2 border-dashed border-[var(--border-primary)] flex flex-col items-center justify-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-emerald-500 hover:bg-emerald-500/5 transition-all"><Camera size={20} /><span className="text-4xs font-black uppercase">{t("auto.adminrouteupload.legg_til", "Legg til")}</span></button>
                  {photoPreviews.map((src, i) => (<div key={i} className="relative aspect-square rounded-2xl overflow-hidden border border-[var(--border-secondary)] group"><img src={src} className="w-full h-full object-cover" alt="" /><button onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1.5 bg-black/60 text-white rounded-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"><Trash2 size={14} /></button></div>))}
                </div>
                <input type="file" multiple accept="image/*" className="hidden" ref={photosInputRef} onChange={handleFileChange} />
              </div>
            ) : (
              // Links UI
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t("auto.adminrouteupload.ekstern_lenke_youtube_blogg", "Ekstern lenke (YouTube / Blogg)")}</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newLinkUrl}
                      onChange={(e) => setNewLinkUrl(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl py-3 px-4 text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!newLinkUrl) return;
                        setIsAnalyzingLink(true);
                        try {
                          const analysis = await analyzeExternalLink(newLinkUrl);
                          const linkEntry: RouteExternalLink = analysis ? {
                            title: analysis.title,
                            url: newLinkUrl,
                            type: analysis.type
                          } : {
                            title: 'Ekstern Lenke',
                            url: newLinkUrl,
                            type: 'other'
                          };

                          setExternalLinks(prev => [...prev, linkEntry]);
                          setNewLinkUrl('');
                        } catch (e) {
                          // Fallback in case of network failure
                          setExternalLinks(prev => [...prev, { title: 'Ekstern Lenke', url: newLinkUrl, type: 'other' }]);
                          setNewLinkUrl('');
                        } finally {
                          setIsAnalyzingLink(false);
                        }
                      }}
                      disabled={isAnalyzingLink || !newLinkUrl}
                      className="px-4 bg-emerald-600 rounded-2xl text-white hover:bg-emerald-500 transition-all flex items-center justify-center"
                    >
                      {isAnalyzingLink ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    </button>
                  </div>

                  <div className="space-y-2 mt-2">
                    {externalLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)]">
                        <div className="text-[var(--text-secondary)]">
                          {link.type === 'video' ? <Video size={14} /> : link.type === 'image' ? <ImageIcon size={14} /> : <LinkIcon size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-2xs font-bold text-[var(--text-primary)] truncate">{link.title}</p>
                          <p className="text-4xs text-[var(--text-muted)] truncate">{link.url}</p>
                        </div>
                        <button onClick={() => setExternalLinks(p => p.filter((_, idx) => idx !== i))} className="text-[var(--text-muted)] hover:text-red-400"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-1 gap-2">
                    <button onClick={() => setUploadStatus('private')} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all text-left ${uploadStatus === 'private' ? getStatusColor('private') : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)] hover:border-[var(--border-primary)]'}`}><div className={`p-2 rounded-xl ${uploadStatus === 'private' ? 'bg-[var(--bg-surface)] text-[var(--text-primary)]' : 'bg-[var(--bg-surface-alt)]'}`}><Lock size={18} /></div><div><p className="text-tiny font-black uppercase tracking-tight">{t("auto.adminrouteupload.privat_rute", "Privat Rute")}</p></div></button>
                    <button onClick={() => setUploadStatus(isAdmin ? 'public' : 'pending')} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all text-left ${uploadStatus !== 'private' ? (isAdmin ? getStatusColor('public') : getStatusColor('pending')) : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)] hover:border-[var(--border-primary)]'}`}><div className={`p-2 rounded-xl ${uploadStatus !== 'private' ? (isAdmin ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white') : 'bg-[var(--bg-surface-alt)]'}`}><Globe size={18} /></div><div><p className="text-tiny font-black uppercase tracking-tight">{isAdmin ? 'Publiser Globalt' : 'Foreslå som Fellesrute'}</p></div></button>
                  </div>
                </div>
              </div>
            )}
            {error && <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl text-red-400 text-2xs font-black uppercase flex gap-3"><AlertTriangle size={16} /> {error}</div>}
          </div>
        </div>

        <div className="p-6 bg-[var(--bg-input)] border-t border-[var(--border-primary)]">
          {step !== 'upload' ? (
            <div className="flex gap-3">
              <button onClick={() => setStep(step === 'photos' ? 'configure' : step === 'links' ? 'photos' : 'upload')} className="px-6 py-4 bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] rounded-2xl font-black text-tiny uppercase tracking-widest">{t("auto.adminrouteupload.tilbake", "Tilbake")}</button>
              {editingRoute && (
                confirmDelete ? (
                  <button onClick={handleDeleteRoute} disabled={isUploading} className="px-4 py-4 bg-red-600 hover:bg-red-500 text-white rounded-2xl font-black text-tiny uppercase tracking-widest flex items-center gap-2 transition-colors disabled:opacity-50">
                    <Trash2 size={14} /> {t("auto.adminrouteupload.bekreft_slett", "Bekreft slett")}
                  </button>
                ) : (
                  <button onClick={() => setConfirmDelete(true)} className="px-4 py-4 bg-[var(--bg-surface-alt)] hover:bg-red-600/20 text-red-400 rounded-2xl font-black text-tiny uppercase tracking-widest flex items-center gap-2 transition-colors">
                    <Trash2 size={14} /> {t("auto.adminrouteupload.slett", "Slett")}
                  </button>
                )
              )}
              {step === 'configure' ? (
                <button onClick={() => { const desc = descriptionRef.current?.value ?? currentSeg.description; if (!desc?.trim()) { setError('Skriv en beskrivelse'); return; } flushInputRefs(); setError(null); setStep('photos'); }} className="flex-1 bg-white text-stone-900 py-4 rounded-2xl font-black text-tiny uppercase tracking-widest flex items-center justify-center gap-2">{t("auto.adminrouteupload.neste", "Neste")} <ChevronRight size={16} /></button>
              ) : step === 'photos' ? (
                <button onClick={() => { setStep('links'); }} className="flex-1 bg-white text-stone-900 py-4 rounded-2xl font-black text-tiny uppercase tracking-widest flex items-center justify-center gap-2">{t("auto.adminrouteupload.neste", "Neste")} <ChevronRight size={16} /></button>
              ) : (
                <button onClick={handleFinalSubmit} disabled={isUploading} className={`flex-1 ${uploadStatus === 'private' ? 'bg-white text-black' : 'bg-emerald-600 text-white'} py-4 rounded-2xl font-black text-tiny uppercase tracking-widest flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 transition-all`}>{isUploading ? <Loader2 size={18} className="animate-spin" /> : editingRoute ? <Save size={18} /> : <CheckCircle2 size={18} />}{isUploading ? 'Lagrer...' : editingRoute ? 'Lagre Endringer' : 'Lagre Rute'}</button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RouteUploadModal;
